import {useReducer} from "react";


/*
 * Hook that keep track of all the state needed to edit the access rules
 * associated with users or groups on resources.
 *
 * The state is composed of three objects:
 * - persistentRules: the rules that are initially in the database
 * - addedRules: the rules that have been added by the user
 * - removedRules: the rules that have been removed by the user
 *
 * Note that only persisted rules can be deleted.
 */
const useAccessRulesEditState = (accessRuleEqualityFn=accessRuleEqualFn) => {
    const [ruleState, setRuleState] = useReducer((state, action) => {
        let {persistentRules, addedRules, removedRules} = state;
        const {type:actionType, accessRule} = action;
        switch (actionType) {
            case 'set':
                if (persistentRules.find(r => accessRuleEqualityFn(r, accessRule))) {
                    return state;
                }
                persistentRules = [...persistentRules, accessRule]
                return {...state, persistentRules};
            case 'toggle':
                const initiallySet = !!persistentRules.find(r => accessRuleEqualityFn(r, accessRule));
                const added = !!addedRules.find(r => accessRuleEqualityFn(r, accessRule));
                const removed = !!removedRules.find(r => accessRuleEqualityFn(r, accessRule));
                const isSet = (initiallySet && !removed) || added;
                if (initiallySet) {
                    if(isSet){
                        const rule = persistentRules.find(r => accessRuleEqualityFn(r, accessRule));
                        if(rule){
                            removedRules = [...removedRules, rule]
                        }
                    }else{
                        removedRules = removedRules.filter(r => !accessRuleEqualityFn(r, accessRule))
                    }
                }
                if (!initiallySet) {
                    if(isSet) {
                        addedRules = addedRules.filter(r => !accessRuleEqualityFn(r, accessRule))
                    }else{
                        addedRules = [...addedRules, accessRule]
                    }
                }
                return {...state, addedRules, removedRules}
            case 'cancel':
                return {...state, addedRules: [], removedRules: []}
            case 'reset':
                return {persistentRules: [], addedRules: [], removedRules: []}
            default:
                throw new Error();
        }
    }, {
        persistentRules: [],
        addedRules: [],
        removedRules: [],
    });

    const hasAccessRule = (accessRule) => {
        const initiallySet = !!ruleState.persistentRules.find(r => accessRuleEqualityFn(r, accessRule));
        const added = !!ruleState.addedRules.find(r => accessRuleEqualityFn(r, accessRule));
        const removed = !!ruleState.removedRules.find(r => accessRuleEqualityFn(r, accessRule));
        return (initiallySet && !removed) || added;
    }

    const setAccessRule = (accessRule) => {
        setRuleState({type: 'set', accessRule});
    }

    const toggleAccessRule = (accessRule) => {
        setRuleState({type: 'toggle', accessRule});
    }

    const cancelChanges = () => {
        setRuleState({type: 'cancel'});
    }

    const reset = () => {
        setRuleState({type: 'reset'});
    }

    // Count the number of rules that are selected for the given permission
    // and list of rules. It doesn't count the internal rules because they
    // might not be visible to the user (other table pages).
    const countSelectedRules = (permission, rules) => {
        if(!Array.isArray(rules)) {
            return 0;
        }
        return rules.filter(r => hasAccessRule(r)).length;
    }

    // Toggle all the given rules for the given permission
    // If there is no rules for the given permission, do nothing.
    const toggleAllRules = (permission, rules) => {
        if (!Array.isArray(rules)) {
            return;
        }
        const areAllRulesSelected = countSelectedRules(permission, rules) === rules.length;
        // All selected -> deselect all
        if (areAllRulesSelected) {
            rules.forEach(r => toggleAccessRule(r));
        } else { // Some selected some not selected -> toggle the ones that are not selected
            rules.forEach(r => {
                if (!hasAccessRule(r)) {
                    toggleAccessRule(r);
                }
            });
        }
    }

    const additionCount = ruleState.addedRules.length;
    const removalCount = ruleState.removedRules.length;
    const changesCount = additionCount + removalCount;
    return {
        ruleState,
        changesCount,
        hasAccessRule,
        setAccessRule,
        toggleAccessRule,
        cancelChanges,
        countSelectedRules,
        toggleAllRules,
        reset};
}

const accessRuleEqualFn = (r1, r2) => {
    return (
        r1.permission === r2.permission
        && r1.resourceId === r2.resourceId
        && r1.userId === r2.userId
        && r1.groupId === r2.groupId
    );
}

export default useAccessRulesEditState;